import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useResizeObserver, useDebounceCallback } from '@buffer-mono/popcorn'

interface UseOverflowItemsOptions<T> {
  items: T[]
  gap: number // Gap between items in pixels
  dependencies?: unknown[] // Additional dependencies to trigger recalculation
}

interface UseOverflowItemsResult<T> {
  visibleItems: T[]
  overflowItems: T[]
  containerRef: (node: HTMLElement | null) => void
  itemRefs: Array<(node: HTMLElement | null) => void>
  allFeedsRef: (node: HTMLElement | null) => void
  collectionEditorRef: (node: HTMLElement | null) => void
  moreButtonRef: (node: HTMLElement | null) => void
}

/**
 * Calculates how many items fit on one row and which ones overflow.
 *
 * Every item stays mounted. The consumer hides the ones that overflow with
 * `visibility: hidden`, which keeps a layout box, so their widths stay
 * measurable. The hook then subtracts the row's fixed parts (the all feeds
 * button, the collection editor, the more button) from the container width and
 * walks the items in order until one no longer fits.
 *
 * ## Important Notes:
 * - Is built specifically for the FeedsNavigation component.
 * - Uses debounced resize observations for performance.
 * - Hide overflowing items with `visibility: hidden`, never `display: none`.
 *
 * @param items - The items to calculate the visible and overflow items for.
 * @param gap - The gap between the items in pixels.
 * @returns The visible and overflow items, plus callback refs for the container, the items, the all feeds button, the collection editor, and the more button.
 */
export function useOverflowItems<T>({
  items,
  gap,
  dependencies = [],
}: UseOverflowItemsOptions<T>): UseOverflowItemsResult<T> {
  const [visibleCount, setVisibleCount] = useState<number>(items.length)

  const containerElementRef = useRef<HTMLElement | null>(null)
  const allFeedsElementRef = useRef<HTMLElement | null>(null)
  const collectionEditorElementRef = useRef<HTMLElement | null>(null)
  const moreButtonElementRef = useRef<HTMLElement | null>(null)
  const itemElementRefs = useRef<(HTMLElement | null)[]>([])

  // Callback refs to capture elements
  const containerRef = useCallback((node: HTMLElement | null) => {
    containerElementRef.current = node
  }, [])

  const allFeedsRef = useCallback((node: HTMLElement | null) => {
    allFeedsElementRef.current = node
  }, [])

  const collectionEditorRef = useCallback((node: HTMLElement | null) => {
    collectionEditorElementRef.current = node
  }, [])

  const moreButtonRef = useCallback((node: HTMLElement | null) => {
    moreButtonElementRef.current = node
  }, [])

  // Create array of callback refs for items
  const itemCount = items.length
  const itemRefs = useMemo<Array<(node: HTMLElement | null) => void>>(() => {
    return Array.from(
      { length: itemCount },
      (_, index) =>
        (node: HTMLElement | null): void => {
          itemElementRefs.current[index] = node
        },
    )
  }, [itemCount])

  // Measurement function
  const measureAndCalculateOverflow = useCallback(() => {
    const container = containerElementRef.current
    const allFeedsElement = allFeedsElementRef.current

    if (!container || !allFeedsElement) {
      return
    }

    // Get widths
    const containerWidth = container.getBoundingClientRect().width
    const allFeedsWidth = allFeedsElement.getBoundingClientRect().width

    // Get collection editor width if it exists (it's conditionally rendered inline with chips)
    const collectionEditorElement = collectionEditorElementRef.current
    const collectionEditorWidth = collectionEditorElement
      ? collectionEditorElement.getBoundingClientRect().width
      : 0

    // Account for the flex gap between its children
    const containerStyle = window.getComputedStyle(container)
    const containerGap = parseFloat(containerStyle.gap) || gap

    // Small buffer to account for subpixel rounding, borders, and browser zoom
    const BUFFER = 5

    // Available width for the chips row. One gap separates the all feeds
    // button from the chips. The second gap only exists once the more button
    // joins the row, so the pass that reserves the button reserves it too.
    const availableWidth =
      containerWidth -
      allFeedsWidth -
      containerGap -
      (collectionEditorWidth > 0 ? collectionEditorWidth + containerGap : 0) - // Collection editor if inline
      BUFFER

    // Measure all items and calculate which ones fit
    // Note: The Flex component handles gaps automatically, so we just need to account
    // for the gap between items (n-1 gaps for n items)
    const itemGap = containerGap
    const countItemsThatFit = (widthLimit: number): number => {
      let accumulatedWidth = 0
      let fitCount = 0

      for (let i = 0; i < items.length; i++) {
        const itemElement = itemElementRefs.current[i]
        if (!itemElement) continue

        const itemWidth = itemElement.getBoundingClientRect().width

        // Check if adding this item (plus gap before it if not first) fits
        const widthNeeded =
          accumulatedWidth + itemWidth + (fitCount > 0 ? itemGap : 0)

        if (widthNeeded <= widthLimit) {
          // Item fits! Add its width plus the gap that comes before it (if not first)
          accumulatedWidth = widthNeeded
          fitCount++
        } else {
          break // All remaining items overflow
        }
      }

      return fitCount
    }

    // Two-pass calculation: the more button only renders when items overflow,
    // so its width is reserved only once the first pass proves overflow exists.
    // A single pass that always reserves would overflow a chip that fits.
    const moreButtonElement = moreButtonElementRef.current
    const moreButtonWidth = moreButtonElement
      ? moreButtonElement.getBoundingClientRect().width
      : 0

    const fitCountWithoutMoreButton = countItemsThatFit(availableWidth)
    const newVisibleCount =
      fitCountWithoutMoreButton >= items.length || moreButtonWidth === 0
        ? fitCountWithoutMoreButton
        : countItemsThatFit(availableWidth - moreButtonWidth - containerGap)

    // Only update state if the visible count actually changed
    setVisibleCount((prev) =>
      prev !== newVisibleCount ? newVisibleCount : prev,
    )
  }, [items.length, gap])

  // Debounce the measurement to avoid excessive calculations during rapid resizes
  // 100ms provides a good balance between responsiveness and performance
  // leading: true ensures immediate first measurement when resize starts
  const debouncedMeasure = useDebounceCallback(
    measureAndCalculateOverflow,
    100,
    { leading: true },
  )

  // The container changes size when the window does, which changes how much
  // room the chips have.
  useResizeObserver<HTMLElement>({
    ref: containerElementRef,
    onResize: debouncedMeasure,
  })

  // The chips change width on their own: a feed title arrives from a refresh,
  // a collection is renamed, a web font swaps in. Every measured element needs
  // watching, the hidden ones included — their width decides when they fit
  // again, and out of flow they resize nothing else that could stand in for
  // them. Without this the count goes stale and the row spills past the clip.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => debouncedMeasure())
    for (const element of [
      ...itemElementRefs.current,
      moreButtonElementRef.current,
    ]) {
      if (element) observer.observe(element)
    }

    return (): void => observer.disconnect()
    // Keyed on items rather than their count: a refresh can swap the whole
    // list for a same-length one, which mounts new elements that this
    // observer would otherwise miss while it watched the detached ones.
  }, [items, debouncedMeasure])

  // Recalculate immediately when items change, when elements mount, or when dependencies change
  useEffect(() => {
    measureAndCalculateOverflow()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, measureAndCalculateOverflow, ...dependencies])

  // Split items into visible and overflow
  const visibleItems = useMemo<T[]>(
    () => items.slice(0, visibleCount),
    [items, visibleCount],
  )

  const overflowItems = useMemo<T[]>(
    () => items.slice(visibleCount),
    [items, visibleCount],
  )

  const result: UseOverflowItemsResult<T> = {
    visibleItems,
    overflowItems,
    containerRef,
    itemRefs,
    allFeedsRef,
    collectionEditorRef,
    moreButtonRef,
  }

  return result
}
